import type { SeededRng } from '../../core/rng';
import { MAX_PLAYERS } from '../../core/players';

export type SpawnPos = { x: number; y: number };

/**
 * Picks a distinct authored start per fighter. The catalog/world choose an arena with enough
 * capacity first: extending a small arena with offsets could put fighters inside cover or off
 * ledges. Preserve legacy column spacing for at most four fighters and the one-metre spawn lift.
 */
export function spawnPositions(
  spawns: readonly SpawnPos[],
  count: number,
  rng: SeededRng,
): SpawnPos[] {
  if (!Number.isInteger(count) || count < 0 || count > MAX_PLAYERS)
    throw new RangeError(`Player count must be between 0 and ${MAX_PLAYERS}.`);
  if (count > spawns.length)
    throw new RangeError(
      `This arena has ${spawns.length} starts for ${count} fighters. Choose a larger arena.`,
    );
  const order = rng.shuffle(spawns.slice());
  const out: SpawnPos[] = [];
  for (const spawn of order.slice(0, count)) {
    let x = spawn.x;
    if (count <= 4) {
      // Older arenas sometimes put starts in one vertical column; keep their original spacing.
      for (let guard = 0; guard < 4; guard++) {
        const clash = out.find((other) => Math.abs(other.x - x) < 0.9);
        if (!clash) break;
        x += x >= clash.x ? 0.9 : -0.9;
      }
    }
    out.push({ x, y: spawn.y + 1 });
  }
  return out;
}
